import i18n from '@utils/i18n'

const MILLS_PER_DAY = 1000 * 60 * 60 * 24
const APRIL_1970 = Math.pow(10, 10)

type MaybeDate = Date | number | string | null | undefined

/**
 * Coerce the given value to a Date. If the value is null, return the current Date
 */
function toDate(date: MaybeDate): Date {
  if (date === null || date === undefined) {
    return new Date()
  }

  switch (typeof date) {
    case 'number':
      // convert dates that are probably in seconds to milliseconds
      return new Date(date < APRIL_1970 ? date * 1000 : date)
    case 'string':
      return new Date(date)
  }
  return date
}

type FormatOptions = {
  monthLong?: boolean
}

/** Formats a Date */
function doFormatDate(date: Date, format?: string | null, options: FormatOptions = {}): string {
  const resourceKey = `date.format.${format ?? 'default'}`
  const monthString = options.monthLong ? 'date.monthLong.' : 'date.month.'

  // Compute all properties, whether or not the format string refers to them
  let y = date.getFullYear()
  if (y < 1900) {
    y += 1900 // in IE8, 1900 is already added in
  }

  const mo = date.getMonth() + 1
  const month = i18n.t(monthString + date.getMonth())

  const d = date.getDate()
  const day = i18n.t(`date.day.${date.getDay()}`)

  const hh = date.getHours()
  let h = date.getHours() % 12
  if (h === 0) h = 12

  let m: number | string = date.getMinutes()
  if (m < 10) {
    m = '0' + m
  }

  const amPm = i18n.t(hh >= 12 ? 'time.pm' : 'time.am')
  return i18n.t(resourceKey, { d, mo, month, y, day, hh, h, m, amPm })
}

function doFormatFutureTime(date: Date, now: Date): string {
  let interval = Math.floor((date.getTime() - now.getTime()) / 1000)
  let unit

  if (interval < 60) unit = 'seconds'
  else if ((interval /= 60) < 60) unit = 'minutes'
  else if ((interval /= 60) < 24) unit = 'hours'
  else if ((interval /= 24) < 30) unit = 'days'
  else return doFormatDate(date)

  return i18n.t(`time.${unit}.fromNow`, { count: Math.floor(interval) })
}

function doFormatPastTime(date: Date, now: Date): string {
  let format = 'time_long_ago'

  // Choose between showing a date and a day. We want to show "today" and "yesterday" as such always.
  // For days less recent but still within the past 6 days, showing "last Monday" (for example) is
  // better than showing a date, but the choice is not super-important. Note that not all days have
  // 24 hours!
  if (now.getTime() - date.getDate() < 6 * MILLS_PER_DAY) {
    switch (now.getDay() - date.getDay()) {
      case 0:
        // Same day
        format = 'time_today'
        break
      case 1:
      case -6:
        format = 'time_yesterday'
        break
      default:
        format = 'time_days_ago'
    }
  } else if (date.getFullYear() === now.getFullYear()) {
    format = 'time_this_year'
  }

  return doFormatDate(date, format)
}

/**
 * Check to see if the given date's time portion is at 00:00 UTC. If so, it was almost certainly a
 * Date and not a DateTime on the server. In which case, convert it to an exact date in the local
 * time zone for conversion purposes
 */
export function fixupDate(dateInput: MaybeDate): Date {
  let date = toDate(dateInput)

  const dateNum = date.getTime()
  if (dateNum % MILLS_PER_DAY === 0)
    date = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  return date
}

/** Format just the date portion of a date for general use */
export function formatDate(date: MaybeDate, options: FormatOptions = {}): string {
  return doFormatDate(fixupDate(date), null, options)
}

/** Format a date/time relative to the current time */
export function formatRelativeTime(date: MaybeDate, now: MaybeDate): string {
  const d = toDate(date)
  const n = toDate(now)
  return (d.getTime() > n.getTime() ? doFormatFutureTime : doFormatPastTime)(d, n)
}

export function formatDayAndMonth(date: Date): string {
  return i18n.t('date.format.dayAndMonth', {
    mo: i18n.t(`date.month.${date.getMonth()}`),
    d: date.getDate(),
  })
}
